import { Dispatch, FC, SetStateAction, Suspense, useEffect, useState } from 'react';
import { LearnosityRequest } from '../../models/LearnosityRequest.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { selectUser } from '../../redux/reducers/userSlice.ts';
import { assessmentRegions } from '../../learnosity-regions/AssessmentRegions.ts';
import { reviewAssessmentRegions } from '../../learnosity-regions/ReviewAssessmentRegions.ts';
import AssessmentWrapper from '../AssessmentWrapper/AssessmentWrapper.tsx';
import { AttemptInformation } from '../../models/AttemptInformation.ts';
import { useLearnosityBffInitialization } from '../../custom-hooks/useLearnosityBffInitialization';
import { AssessmentState, DataLrnSessionBySkill } from '../../custom-hooks/useLrnSession.ts';
import { SkillResourceSteps } from '../../models/Timeline.ts';
import { PreviousAttempts } from '../../models/PreviousAttempts.ts';
import { EssentialSkills } from '../../models/SupportSkills.ts';
import { getIncompleteSkills } from '../../utils/getIncompleteSkills.ts';
import { PreviousAndNextSSS } from '../../redux/reducers/studentSkillAfterTakeAssessmentSlice.ts';
import { useAuth0 } from '@auth0/auth0-react';
import { useComponentTracking } from '../../hooks/useComponentTracking.ts';
// import AssessmentCompletedReviewDialog from '../AssessmentCompleted/AssessmentCompletedReviewDialog.tsx';
import { AssessmentCompleted } from '../AssessmentCompleted/AssessmentCompleted.tsx';
import { ThreeDotLoader } from '../Fallback/ThreeDotsLoader.tsx';
import { ErrorBoundaryUI } from '../ErrorBoundary/ErrorBoundaryUI.tsx';
import { PreviousStatus } from '../StudentSingleSkill/StudentSingleSkill.tsx';
import SkillResourceHeaderLearnosityWrapper from '../SkillResource/SkillResourceHeaderLearnosityWrapper.tsx';
import { GoalValue } from '../WeeklyGoal/WeeklyGoalWrapper.tsx';
import { PreSkillChallenge } from '../SkillResource/PreSkillChallenge.tsx';
import { useParams } from 'react-router-dom';
import { useCooldown } from '../../contexts/RetakeCountdownContext.tsx';
import RetakeCountdownWrapperUI from '../RetakeCountdown/RetakeCountdownWrapperUI.tsx';
import { useIsCountdownFlagActive } from '../../businessLogicService/retakeCountdownHelper.ts';
import { isWeeklyGoalEligible } from '../../businessLogicService/weeklyGoalEligibility.ts';
import { usePostHog } from '@posthog/react';
import { isAbortError } from '../../utils/fetchCancellation';

export type ChallengePhase = 'COOLDOWN' | 'PRE' | 'ASSESSMENT';
interface Props {
  title: string;
  lrnSessionId: DataLrnSessionBySkill;
  setCurrentStatus: (value: { classname: string; status: string | undefined } | null) => void;
  refetchLrnSession: () => void;
  setAssessmentState: (state: AssessmentState) => void;
  setAttemptInformation: (state: AttemptInformation | null) => void;
  attemptInformation: AttemptInformation | null;
  previousAttempts: PreviousAttempts[];
  setPreviousAttempts: Dispatch<SetStateAction<PreviousAttempts[]>>;
  essentialSkills: EssentialSkills[];
  // loadPreviousSession: (session: string, id: string) => void;
  workedExampleUrl: string | null | undefined;
  sssAfterTakeAssessment: PreviousAndNextSSS;
  onMoveForward?: () => void;
  moveForwardLabel?: string;
  setCurrentStep: Dispatch<SetStateAction<SkillResourceSteps | null>>;
  skipNewActivityId: boolean;
  setSkipNewActivityId: Dispatch<SetStateAction<boolean>>;
  setPreviousStatus: Dispatch<SetStateAction<PreviousStatus[]>>;
  setDisabledReviewModal: Dispatch<SetStateAction<boolean>>;
  weeklyGoalOverrideData?: GoalValue | null;
  setAssessmentInProgress: Dispatch<SetStateAction<boolean>>;
  assessmentInProgress: boolean;
  skillId?: string;
  /** Substate as of page load, before this attempt was submitted. See SkillResource. */
  preAttemptSubstateId: string | null;
}

export const AssessmentComponent: FC<Props> = ({
  title,
  lrnSessionId,
  setCurrentStatus,
  refetchLrnSession,
  setAssessmentState,
  attemptInformation,
  setAttemptInformation,
  setCurrentStep,
  previousAttempts,
  setPreviousAttempts,
  essentialSkills,
  // loadPreviousSession,
  workedExampleUrl,
  sssAfterTakeAssessment,
  onMoveForward,
  moveForwardLabel,
  skipNewActivityId,
  setSkipNewActivityId,
  setPreviousStatus,
  setDisabledReviewModal,
  weeklyGoalOverrideData,
  setAssessmentInProgress,
  assessmentInProgress,
  skillId,
  preAttemptSubstateId,
}) => {
  const { skillCode: skillNumber } = useParams();
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [reviewDialogVisible, setReviewDialogVisible] = useState<boolean>(false);
  const [request, setRequest] = useState<LearnosityRequest | null>(null);
  const [loadingAssessment, setloadingAssessment] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const user = useAppSelector(selectUser);
  const { getAccessTokenSilently, logout } = useAuth0();
  // Eligible only if the student has never earned credit for this skill before — neither by
  // passing its assessment nor by inference from a diagnostic or a related assessment. At this
  // point previousAttempts does not yet include the attempt being taken.
  const isWeeklyGoalElegible = isWeeklyGoalEligible(previousAttempts, preAttemptSubstateId);

  const cooldownCtx = useCooldown();
  const remainingTime = cooldownCtx?.getRemainingTime(skillNumber || '') || 0;
  const isCooldownActive = remainingTime > 0;
  const isRetakeCountdownFlagActive = useIsCountdownFlagActive();
  const [phase, setPhase] = useState<ChallengePhase>(
    isCooldownActive && isRetakeCountdownFlagActive ? 'COOLDOWN' : 'PRE',
  );
  const posthog = usePostHog();
  const { fetchInitialization, refetchInitialization } = useLearnosityBffInitialization(
    getAccessTokenSilently,
    logout,
    posthog,
  );

  const trackingName =
    lrnSessionId.state === 'initial'
      ? 'Skill Challenge - Started'
      : lrnSessionId.state === 'resume'
      ? 'Skill Challenge - Resumed'
      : 'Skill Challenge - Review';

  useComponentTracking(trackingName);

  const fetchInitializationObject = (signal: AbortSignal) =>
    fetchInitialization(
      {
        userId: user?.sub ?? '',
        state: lrnSessionId.state,
        sessionId: lrnSessionId.sessionId,
        activityId: lrnSessionId.activityId,
        regions: lrnSessionId.state === 'review' ? reviewAssessmentRegions : assessmentRegions,
        title,
        renderingType: 'assess',
        showCorrectAnswers: lrnSessionId.showIncorrectAnswers,
        autoSaveAnswers: false,
        responsiveRegions: false,
        showIntro: false,
        scrollToTest: false,
        scrollToTop: false,
        warningOnChange: false,
      },
      { signal },
    );

  useEffect(() => {
    const controller = new AbortController();
    setRequest(null);
    if (user && user.sub) {
      setLoadError(false);
      setloadingAssessment(skipNewActivityId ? false : true);
      fetchInitializationObject(controller.signal)
        .then(data => {
          if (controller.signal.aborted) return;
          setRequest(data);
        })
        .catch(error => {
          if (controller.signal.aborted || isAbortError(error)) return;
          // The BFF call already retried with backoff (fetchWithRetry) and still failed.
          // Clear the loading state so we don't spin forever, and flag the error so the
          // render can surface it to the user.
          setloadingAssessment(false);
          setLoadError(true);
        });
    }
    return () => controller.abort();
  }, [
    lrnSessionId.sessionId,
    lrnSessionId.state,
    lrnSessionId.activityId,
    lrnSessionId.showIncorrectAnswers,
    user?.sub,
    title,
  ]);

  return (
    <>
      {assessmentInProgress && phase === 'PRE' && <PreSkillChallenge onFinish={() => setPhase('ASSESSMENT')} />}
      <div
        className={`tw-w-full nc_student-skill-content-container
          ${phase === 'ASSESSMENT' && lrnSessionId.state !== 'review' ? 'tw-mt-10' : ''}
          ${phase === 'COOLDOWN' && lrnSessionId.state !== 'review' ? 'tw-max-h-[537px]' : ''}
        `}
        style={{ height: '-webkit-fill-available' }}
      >
        {request && user && user.sub && !attemptInformation && !alreadySubmitted && !loadError && (
          <Suspense fallback={<ThreeDotLoader dark />}>
            {phase === 'COOLDOWN' && (
              <RetakeCountdownWrapperUI
                skillCode={skillNumber || ''}
                setAssessmentInProgress={setAssessmentInProgress}
                setSkipNewActivityId={setSkipNewActivityId}
                setDisabledReviewModal={setDisabledReviewModal}
                refetchLrnSession={refetchLrnSession}
                setPhase={setPhase}
              />
            )}

            {phase === 'ASSESSMENT' && (
              <AssessmentWrapper
                setAlreadySubmitted={setAlreadySubmitted}
                setPreviousAttempts={setPreviousAttempts}
                setCurrentStatus={setCurrentStatus}
                setLoading={setloadingAssessment}
                setAttemptInfo={setAttemptInformation}
                sessionId={lrnSessionId.sessionId}
                changeState={setAssessmentState}
                onFailure={() => {
                  setloadingAssessment(false);
                  setLoadError(true);
                }}
                request={request!}
                refetchInitialization={refetchInitialization}
                previousAttempts={previousAttempts}
                setPreviousStatus={setPreviousStatus}
                setDisabledReviewModal={setDisabledReviewModal}
                isWeeklyGoalElegible={isWeeklyGoalElegible}
                weeklyGoalOverrideData={weeklyGoalOverrideData}
                setAssessmentInProgress={setAssessmentInProgress}
                setCurrentStep={setCurrentStep}
                skillId={skillId}
              />
            )}
          </Suspense>
        )}
        {loadingAssessment && !request && !attemptInformation && <ThreeDotLoader dark />}
        {loadError && !attemptInformation && <ErrorBoundaryUI embedded />}
        {lrnSessionId.state === 'review' && attemptInformation && !alreadySubmitted && (
          <Suspense fallback={<ThreeDotLoader dark />}>
            <AssessmentCompleted
              setAssessmentInProgress={setAssessmentInProgress}
              workedExampleUrl={workedExampleUrl}
              setCurrentStep={setCurrentStep}
              previousAttempts={previousAttempts}
              refetchLrnSession={refetchLrnSession}
              setAttemptInformation={setAttemptInformation}
              attemptInformation={attemptInformation!}
              incompleteEssentialSkills={getIncompleteSkills(essentialSkills)}
              sssAfterTakeAssessment={sssAfterTakeAssessment}
              setReviewDialogVisible={setReviewDialogVisible}
              onMoveForward={onMoveForward}
              moveForwardLabel={moveForwardLabel}
              setSkipNewActivityId={setSkipNewActivityId}
              skipNewActivityId={skipNewActivityId}
              setPhase={setPhase}
              preAttemptSubstateId={preAttemptSubstateId}
            />
          </Suspense>
        )}
        {alreadySubmitted && (
          <div className="nc_block tw-w-full tw-h-[20rem] tw-flex tw-flex-col">
            <h3 className="tw-text-center tw-my-auto">Assessment already submitted!</h3>
          </div>
        )}
      </div>
      {reviewDialogVisible && previousAttempts && (
        <SkillResourceHeaderLearnosityWrapper
          lrnData={{
            sessionId: previousAttempts[0].sessionId ?? '',
            state: 'review',
            activityId: previousAttempts[0].activityId ?? '',
            showIncorrectAnswers: true,
          }}
          setOpenLearnosityModal={setReviewDialogVisible}
        />
      )}
    </>
  );
};
